import type { NpcIdentity, Quest, QuestRejected } from '@shefira/shared';

import type { CssAtlas } from '../assets/dom-atlas.js';
import { closeButton } from './hud.js';
import { hideItemHover } from './item-hover.js';
import { element, kitIcon, kitTitle } from './kit.js';
import type { QuestEntry, QuestLog } from './quest-log.js';
import { questGrants, questRewards } from './quest-rewards.js';
import { greetingOf, npcName, objectiveLine, QUEST_REFUSALS } from './quest-text.js';
import { uiFormat, uiText } from './text.js';
import './smith.css';
import './quests.css';

/**
 * A word with a townsperson (ADR 0047): what they have to say for the
 * quests, one subject at a time. A quest finished comes first, then their
 * words for a quest under way, the quests they offer, those under way they
 * take back, and those that wait for a level. A keeper with a trade (the
 * smith) opens his own window from here, or directly when he has nothing.
 * Like every panel it only sends intents; the journal that comes back
 * redraws it.
 */

export interface QuestDialogOptions {
  log: QuestLog;
  icons: () => CssAtlas | null;
  onAccept: (questId: string, npcId: string) => void;
  onTurnIn: (questId: string, npcId: string) => void;
}

/** The townsperson spoken to: their entity, what they are, and their trade if they keep one. */
export interface DialogNpc extends NpcIdentity {
  id: string;
  service?: { label: string; open: () => void };
}

type Subject =
  | { kind: 'ready'; entry: QuestEntry }
  | { kind: 'talk'; quest: Quest; index: number }
  | { kind: 'offer'; quest: Quest }
  | { kind: 'pending'; entry: QuestEntry }
  | { kind: 'upcoming'; quest: Quest };

function questOf(subject: Subject): Quest {
  return 'entry' in subject ? subject.entry.quest : subject.quest;
}

/** A word heard: kept on screen while the dialogue is open, though the server has flagged it. */
interface Heard {
  quest: Quest;
  index: number;
}

function keyOf(subject: Subject): string {
  return `${subject.kind}:${questOf(subject).id}:${subject.kind === 'talk' ? String(subject.index) : ''}`;
}

export class QuestDialog {
  private readonly root = element('section', 'smith quest-dialog');
  private readonly title = element('h2', 'smith__title quest-dialog__title');
  private readonly name = element('span', 'quest-dialog__name');
  private readonly subjects = element('div', 'quest-dialog__subjects');
  private readonly body = element('div', 'quest-dialog__body');
  private readonly actions = element('div', 'quest-dialog__actions');
  private readonly message = element('p', 'smith__message');
  private npc: DialogNpc | undefined;
  private selected: string | undefined;
  private heard: Heard[] = [];
  private pending = false;
  private readonly offLog: () => void;
  private readonly onKey = (event: KeyboardEvent): void => {
    if (event.key === 'Escape' && !this.root.hidden) this.close();
  };

  constructor(private readonly options: QuestDialogOptions) {
    this.root.hidden = true;
    kitTitle(this.title, 'quests');
    this.title.append(
      this.name,
      closeButton('Fermer (Échap)', () => {
        this.close();
      }),
    );
    this.message.setAttribute('aria-live', 'polite');
    const scroll = element('div', 'quest-dialog__scroll');
    scroll.append(this.subjects, this.body);
    this.root.append(this.title, scroll, this.actions, this.message);
    document.body.append(this.root);
    window.addEventListener('keydown', this.onKey);
    this.offLog = options.log.onChange(() => {
      this.pending = false;
      if (this.isOpen) this.render();
    });
  }

  get isOpen(): boolean {
    return !this.root.hidden;
  }

  /** The townsperson now spoken to, if the dialogue is open. */
  get npcId(): string | undefined {
    return this.isOpen ? this.npc?.id : undefined;
  }

  open(npc: DialogNpc): void {
    this.npc = npc;
    this.selected = undefined;
    this.heard = this.options.log
      .agendaOf(npc)
      .talks.map(({ entry, index }) => ({ quest: entry.quest, index }));
    this.pending = false;
    this.message.textContent = '';
    delete this.message.dataset.tone;
    this.name.textContent = uiText(npcName(npc.mob));
    this.root.setAttribute('aria-label', uiText(npcName(npc.mob)));
    this.root.hidden = false;
    this.render();
  }

  close(): void {
    hideItemHover();
    this.root.hidden = true;
    this.npc = undefined;
  }

  refused(reason: QuestRejected['reason']): void {
    this.pending = false;
    if (!this.isOpen) return;
    this.message.textContent = uiText(QUEST_REFUSALS[reason]);
    this.message.dataset.tone = 'refused';
    this.render();
  }

  private agenda(): Subject[] {
    const npc = this.npc;
    if (npc === undefined) return [];
    const agenda = this.options.log.agendaOf(npc);
    return [
      ...agenda.ready.map((entry): Subject => ({ kind: 'ready', entry })),
      ...this.heard.map(({ quest, index }): Subject => ({ kind: 'talk', quest, index })),
      ...agenda.offers.map((quest): Subject => ({ kind: 'offer', quest })),
      ...agenda.pending.map((entry): Subject => ({ kind: 'pending', entry })),
      ...agenda.upcoming.map((quest): Subject => ({ kind: 'upcoming', quest })),
    ];
  }

  private render(): void {
    const npc = this.npc;
    if (npc === undefined) return;
    const subjects = this.agenda();
    const current = subjects.find((subject) => keyOf(subject) === this.selected) ?? subjects[0];
    this.selected = current === undefined ? undefined : keyOf(current);

    // Several subjects: a row of them to pick from; one needs no list.
    this.subjects.replaceChildren();
    this.subjects.hidden = subjects.length < 2;
    for (const subject of subjects) {
      const button = element('button', 'quest-dialog__subject');
      button.type = 'button';
      if (keyOf(subject) === this.selected) button.classList.add('quest-dialog__subject--on');
      const icon =
        subject.kind === 'offer' || subject.kind === 'upcoming' ? 'quest-available' : 'quest-ready';
      const mark = kitIcon(icon);
      if (subject.kind === 'pending' || subject.kind === 'upcoming')
        mark.classList.add('quest-mark--grey');
      button.append(mark, element('span', 'quest-dialog__subject-name', questOf(subject).name));
      button.addEventListener('click', () => {
        this.selected = keyOf(subject);
        this.message.textContent = '';
        this.render();
      });
      this.subjects.append(button);
    }

    this.body.replaceChildren();
    this.actions.replaceChildren();
    if (current === undefined) {
      this.body.append(element('p', 'quest-dialog__line', greetingOf(npc.mob, npc.zone, this.options.log)));
    } else {
      this.renderSubject(current, npc);
    }
    if (npc.service !== undefined) {
      const service = npc.service;
      const go = element('button', 'smith__go quest-dialog__service', service.label);
      go.type = 'button';
      go.addEventListener('click', () => {
        this.close();
        service.open();
      });
      this.actions.append(go);
    }
    this.actions.hidden = this.actions.childElementCount === 0;
  }

  private renderSubject(subject: Subject, npc: DialogNpc): void {
    const quest = questOf(subject);
    this.body.append(element('h3', 'quest-dialog__quest', quest.name));
    switch (subject.kind) {
      case 'ready':
        this.body.append(element('p', 'quest-dialog__line', quest.complete));
        this.body.append(questRewards(quest, this.options.icons()));
        this.button('Rendre la quête', () => {
          this.options.onTurnIn(quest.id, npc.id);
        });
        break;
      case 'talk': {
        const objective = quest.objectives[subject.index];
        if (objective?.kind === 'talk') {
          this.body.append(element('p', 'quest-dialog__line', objective.line));
        }
        break;
      }
      case 'offer':
        this.body.append(element('p', 'quest-dialog__line', quest.offer));
        this.body.append(this.objectives(quest));
        this.body.append(
          ...[questGrants(quest, this.options.icons())].filter((box) => box !== undefined),
        );
        this.body.append(questRewards(quest, this.options.icons()));
        this.button('Accepter', () => {
          this.options.onAccept(quest.id, npc.id);
        });
        break;
      case 'pending':
        this.body.append(element('p', 'quest-dialog__line', quest.pending));
        this.body.append(this.objectives(quest, subject.entry));
        break;
      case 'upcoming':
        this.body.append(element('p', 'quest-dialog__line', quest.offer));
        this.body.append(
          element(
            'p',
            'smith__warn',
            uiFormat('Reviens me voir au niveau {level} : c’est encore trop dangereux pour toi.', {
              level: quest.level,
            }),
          ),
        );
        break;
    }
  }

  private button(label: string, run: () => void): void {
    const button = element('button', 'smith__go quest-dialog__go', label);
    button.type = 'button';
    button.disabled = this.pending;
    button.addEventListener('click', () => {
      if (this.pending) return;
      this.pending = true;
      this.message.textContent = '';
      run();
      this.render();
    });
    this.actions.prepend(button);
  }

  private objectives(quest: Quest, entry?: QuestEntry): HTMLElement {
    const list = element('ul', 'quest-objectives');
    quest.objectives.forEach((_, index) => {
      const progress = entry?.progress[index];
      const done = progress !== undefined && progress.value >= progress.target;
      const item = element('li', 'quest-objectives__item', objectiveLine(quest, index, progress));
      if (done) item.classList.add('quest-objectives__item--done');
      list.append(item);
    });
    return list;
  }

  destroy(): void {
    this.offLog();
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }
}
